import { useMemo } from 'react'

import {
  type TrackReferenceOrPlaceholder,
  setupTrackMutedIndicator,
  getTrackReferenceId,
} from '@livekit/components-core'

import { useEnsureTrackRef } from '@/src/context'

import { useObservableState } from '@/src/hooks/internal'

interface TrackMutedIndicatorReturnType {
  isMuted: boolean
  className: string
}

/**
 * The `useTrackMutedIndicator` hook is used to implement the `TrackMutedIndicator` component
 * and returns the muted state of the given track.
 *
 * @example
 * ```tsx
 * const { isMuted } = useTrackMutedIndicator(trackRef);
 * ```
 * @public
 */
export function useTrackMutedIndicator(
  trackRef?: TrackReferenceOrPlaceholder,
): TrackMutedIndicatorReturnType {
  const trackReference = useEnsureTrackRef(trackRef)

  const { className, mediaMutedObserver } = useMemo(
    () => setupTrackMutedIndicator(trackReference),
    [getTrackReferenceId(trackReference)],
  )

  const isMuted = useObservableState(
    mediaMutedObserver,
    !!(
      trackReference.publication?.isMuted ||
      trackReference.participant.getTrackPublication(trackReference.source)?.isMuted
    ),
  )

  return { isMuted, className }
}
